import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Button, useToast } from '@aps/ui';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { ImageCropDialog } from './ImageCropDialog.js';

/** Server bilan bir xil: JPEG, PNG, WebP, AVIF; ≤ 12 MB. */
export const BANNER_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'] as const;
export const BANNER_IMAGE_MAX_BYTES = 12 * 1024 * 1024;

/** Faylni yuborishdan OLDIN tekshiradi (server baribir qayta tekshiradi). */
export function bannerImageProblem(file: Pick<File, 'type' | 'size'>): 'type' | 'size' | null {
  if (!(BANNER_IMAGE_TYPES as readonly string[]).includes(file.type)) return 'type';
  if (file.size > BANNER_IMAGE_MAX_BYTES) return 'size';
  return null;
}

/** Forma ichidagi rasm tanlovi: serverga faqat "Saqlash" bosilganda yuboriladi. */
export interface BannerImageChoice {
  /** Yangi (kesilgan) fayl; `null` — o'zgarmagan. */
  file: File | null;
  /** Mavjud rasm olib tashlanadi. */
  removed: boolean;
}

export const NO_IMAGE_CHANGE: BannerImageChoice = { file: null, removed: false };

/** Hozir ko'rinadigan rasm manzili: yangi fayl > mavjud rasm > yo'q. */
export function useBannerImagePreview(
  currentUrl: string | null,
  choice: BannerImageChoice,
): string | null {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (choice.file === null) {
      setBlobUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(choice.file);
    setBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [choice.file]);
  if (choice.file !== null) return blobUrl;
  return choice.removed ? null : currentUrl;
}

/**
 * Banner rasmi bloki. Rasm yaratish paytida ham tanlanadi (banner hali yo'q bo'lsa ham):
 * fayl kesiladi, ko'rinishi darhol chiqadi, serverga esa banner saqlanganda yuboriladi.
 */
export function BannerImageField({
  previewUrl,
  choice,
  onChange,
  disabled,
}: {
  previewUrl: string | null;
  choice: BannerImageChoice;
  onChange: (next: BannerImageChoice) => void;
  disabled: boolean;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  // Tanlangan fayl kesish oynasida turadi; tanlov faqat u yopilgach o'zgaradi.
  const [cropping, setCropping] = useState<File | null>(null);

  const onPick = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    // Bir xil faylni qayta tanlash ham `change` bersin.
    event.target.value = '';
    if (file === undefined) return;
    const problem = bannerImageProblem(file);
    if (problem !== null) {
      toast.show(problem === 'type' ? t.refBannerImageType : t.refBannerImageTooBig, 'danger');
      return;
    }
    setCropping(file);
  };
  const choose = (file: File): void => {
    setCropping(null);
    onChange({ file, removed: false });
  };
  const open = (): void => {
    input.current?.click();
  };

  return (
    <div className="adm-banner-photo">
      <input
        ref={input}
        type="file"
        accept={BANNER_IMAGE_TYPES.join(',')}
        hidden
        aria-label={t.refBannerImage}
        onChange={onPick}
      />

      {previewUrl === null ? (
        <button
          type="button"
          className="adm-banner-photo__empty"
          disabled={disabled}
          onClick={open}
        >
          <span className="adm-banner-photo__plus" aria-hidden="true">
            +
          </span>
          <strong>{t.refBannerImageUpload}</strong>
          <span className="aps-muted">{t.refBannerImageEmpty}</span>
        </button>
      ) : (
        <>
          <img
            className="adm-banner-photo__img"
            src={previewUrl}
            alt={t.refBannerImage}
            width={1200}
            height={620}
          />
          <div className="adm-banner-photo__actions">
            <Button size="sm" variant="secondary" disabled={disabled} onClick={open}>
              {t.refBannerImageReplace}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled}
              onClick={() => {
                onChange({ file: null, removed: true });
              }}
            >
              {t.refBannerImageRemove}
            </Button>
            {choice.file !== null && <span className="aps-muted">{t.refBannerImagePending}</span>}
          </div>
        </>
      )}
      <p className="aps-muted adm-banner-photo__hint">{t.refBannerImageHint}</p>

      {cropping !== null && (
        <ImageCropDialog
          key={cropping.name + String(cropping.size)}
          file={cropping}
          position={{ index: 1, total: 1 }}
          aspects={['banner']}
          frameLongSide={460}
          onDone={choose}
          onSkip={() => {
            choose(cropping);
          }}
          onCancel={() => {
            setCropping(null);
          }}
        />
      )}
    </div>
  );
}
